import {
  ArrowLeftRight,
  CircleCheckBig,
  KeyRound,
  Pencil,
  PlayCircle,
  RefreshCw,
  ShieldAlert,
  ShoppingCart,
  UserRound,
} from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import type { QueryView } from '@krp/client-runtime'
import { ActionFieldset, Button, TextField } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import '../../../../shared/ui/aqsi.css'
import type { CashierOptionVm, CodeVm, RegisterRowVm, RmkActions, RmkVm, StartVm } from '../../model/types'
import { FeedbackHelp } from '../FeedbackHelp'
import { PriceTypePicker } from '../PriceTypePicker'
import { useNow, waitText } from '../useNow'
import { JournalDialog } from './JournalDialog'
import { OwnCashierStep } from './OwnCashierStep'
import { RegisterCard } from './RegisterCard'
import { ReleaseDialog } from './ReleaseDialog'
import '../rmk.css'

type StepId = 'cashier' | 'code' | 'price' | 'register'

const isReady = (view: QueryView) => view.status === 'ready' || view.status === 'empty'

/** Строка свёрнутого шага: «Кассир: Иванова А. · изменить». */
function Summary({
  label,
  value,
  note,
  status,
  onChange,
  changeLabel,
}: {
  label: string
  value: ReactNode
  note?: string | null
  /** Подтверждение в той же строке: зелёная галочка «код подтверждён» либо предупреждение «код не подтверждён». */
  status?: { ok: boolean; text: string }
  onChange: (() => void) | null
  changeLabel: string
}) {
  return (
    <div className="rmk-step__summary">
      <p className="aqsi-card__text rmk-step__line" data-state={status === undefined ? 'ok' : status.ok ? 'ok' : 'pending'}>
        {status?.ok === false ? <ShieldAlert size={16} aria-hidden="true" /> : <CircleCheckBig size={16} aria-hidden="true" />}
        <span>
          {label}: <strong>{value}</strong>
          {status !== undefined && <span className="rmk-step__status"> · {status.text}</span>}
        </span>
        {onChange !== null && (
          <Button variant="ghost" icon={<Pencil size={14} aria-hidden="true" />} actionId="aqsi.rmk.change-step" aria-label={changeLabel} onClick={onChange}>
            изменить
          </Button>
        )}
      </p>
      {note !== undefined && note !== null && (
        <p className="aqsi-card__text" role="note">
          {note}
        </p>
      )}
    </div>
  )
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <li className="aqsi-panel rmk-step" aria-label={title}>
      <h3>
        <span className="rmk-step__number" aria-hidden="true">
          {number}
        </span>
        {title}
      </h3>
      {children}
    </li>
  )
}

function loginHint(option: CashierOptionVm): string | null {
  if (option.login === 'pin') return 'нужен код'
  if (option.login === 'unsupported') return 'особый вход'
  return null
}

/** Блок 1. Кассир: список кассиров ЭТОГО источника (живое чтение кабинета); «как в прошлый раз» отмечен. */
function CashierStep({ start, actions, onDone }: { start: StartVm; actions: RmkActions; onDone: () => void }) {
  const [search, setSearch] = useState('')
  const group = useId()
  const cashiers = start.cashiers
  const needle = search.trim().toLowerCase()
  const options = needle === '' ? cashiers.options : cashiers.options.filter((option) => option.name.toLowerCase().includes(needle))
  if (!isReady(cashiers.query)) {
    return <QueryState requestIdHidden query={cashiers.query} what="Кассиры" emptyText="Кассиров нет" onRetry={actions.reload} />
  }
  if (cashiers.empty) {
    return (
      <>
        <p className="aqsi-card__text">В кабинете AQSI нет кассиров — заведите их в приложении AQSI или в кабинете.</p>
        <p className="aqsi-card__text">Пока их нет, можно работать под своим именем: оно попадёт в чек и в команду смены.</p>
        <div className="aqsi-card__actions">
          <Button
            variant="primary"
            icon={<UserRound size={16} aria-hidden="true" />}
            actionId="aqsi.rmk.own-name"
            onClick={() => {
              actions.chooseOwnName()
              onDone()
            }}
          >
            Работать под своим именем
          </Button>
        </div>
      </>
    )
  }
  return (
    <>
      {start.resetNote !== null && (
        <p className="aqsi-card__text" role="note">
          <ShieldAlert size={14} aria-hidden="true" /> {start.resetNote}
        </p>
      )}
      {cashiers.searchable && <TextField label="Найти кассира" type="search" value={search} onChange={setSearch} />}
      <ActionFieldset actionId="aqsi.rmk.choose-cashier" legend="Кассир" className="aqsi-form rmk-choice">
        {options.map((option) => (
          <label key={option.id} className="krp-check rmk-choice__item" data-selected={option.selected || undefined}>
            <input
              type="radio"
              name={group}
              checked={option.selected}
              onChange={() => {
                actions.chooseCashier(option.id)
                onDone()
              }}
            />{' '}
            <span className="rmk-choice__name">{option.name}</span>
            {option.position !== null && <span className="aqsi-card__text"> — {option.position}</span>}
            {loginHint(option) !== null && (
              <span className="rmk-choice__badge">
                <KeyRound size={12} aria-hidden="true" /> {loginHint(option)}
              </span>
            )}
            {option.lastTime && <span className="rmk-choice__badge">как в прошлый раз</span>}
          </label>
        ))}
        {options.length === 0 && <p className="aqsi-card__text">Никого не нашли — проверьте написание.</p>}
      </ActionFieldset>
    </>
  )
}

/**
 * Блок 2. Код — только если служба источника ответила, что этому кассиру нужен вход. Поле скрыто, цифровая клавиатура на
 * телефоне, без автозаполнения; Enter — «Проверить». Введённое стирается сразу после отправки: код нигде не хранится.
 */
function CodeStep({ code, actions }: { code: CodeVm; actions: RmkActions }) {
  const [value, setValue] = useState('')
  const id = useId()
  const now = useNow(1000, code.state === 'locked')
  const lockLeft = code.lockedUntil !== null ? Date.parse(code.lockedUntil) - now : 0
  const locked = code.state === 'locked' && lockLeft > 0
  const checking = code.state === 'checking'
  const unsupported = code.state === 'unsupported'
  const off = locked || checking || unsupported
  const submit = () => {
    if (off || value.trim() === '') return
    actions.verifyCode(value)
    setValue('')
  }
  const message =
    code.state === 'wrong'
      ? code.attemptsLeft === null
        ? 'Код не подошёл.'
        : `Код не подошёл. Осталось попыток: ${code.attemptsLeft}`
      : locked && code.lockedUntil !== null
        ? `Слишком много попыток, повторите через ${waitText(code.lockedUntil, now)}`
        : code.state === 'checking'
          ? 'Проверяем код…'
          : code.message
  return (
    <>
      {unsupported ? (
        <p className="aqsi-card__text" role="alert">
          <ShieldAlert size={14} aria-hidden="true" /> {code.message}
        </p>
      ) : (
        <form
          className="aqsi-form rmk-code"
          aria-label={`Ввод кода кассира ${code.cashierName}`}
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <label className="krp-field__label" htmlFor={id}>
            Код кассира {code.cashierName}
          </label>
          <div className="rmk-code__row">
            <input
              id={id}
              className="rmk-code__input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              data-lpignore="true"
              data-1p-ignore="true"
              value={value}
              disabled={off}
              onChange={(event) => setValue(event.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              icon={<KeyRound size={16} aria-hidden="true" />}
              actionId="aqsi.rmk.verify-code"
              busy={checking}
              disabled={off || value.trim() === ''}
              disabledReason={locked ? 'Кассир заблокирован: подождите или выберите другого.' : 'Введите код кассира.'}
            >
              Проверить
            </Button>
          </div>
        </form>
      )}
      {message !== null && !unsupported && (
        <p className="aqsi-card__text" role={code.state === 'checking' ? 'status' : 'alert'} data-state={code.state}>
          {code.state === 'wrong' || locked || code.state === 'error' || code.state === 'expired' ? (
            <ShieldAlert size={14} aria-hidden="true" />
          ) : null}{' '}
          {message}
        </p>
      )}
    </>
  )
}

/** Блок 4. Касса: кассы магазина — выбирается свободная или касса с открытой ничьей сменой; занятые недоступны с причиной. */
function RegisterStep({ vm, actions, onDone }: { vm: RmkVm; actions: RmkActions; onDone: () => void }) {
  const [releasing, setReleasing] = useState<number | null>(null)
  const group = useId()
  const { query, rows, selected } = vm.start.registers
  if (!isReady(query)) {
    return <QueryState requestIdHidden query={query} what="Кассы" emptyText="Касс нет" onRetry={actions.reload} />
  }
  if (rows.length === 0) {
    return (
      <p className="aqsi-card__text">
        В кабинете нет касс этого магазина. Кассу подключают на самом устройстве — здесь она появится сама.
      </p>
    )
  }
  const releaseRow: RegisterRowVm | undefined = releasing === null ? undefined : rows.find((row) => row.deviceId === releasing)
  return (
    <>
      <ActionFieldset actionId="aqsi.rmk.choose-register" legend="Касса" className="aqsi-form rmk-choice">
        <ul className="rmk-registers" aria-label="Кассы магазина">
          {rows.map((row) => (
            <RegisterCard
              key={row.deviceId}
              row={row}
              selected={selected?.deviceId === row.deviceId}
              pick={
                row.selectable ? (
                  <label className="aqsi-card__title rmk-register__pick">
                    <input
                      type="radio"
                      name={group}
                      checked={selected?.deviceId === row.deviceId}
                      onChange={() => {
                        actions.chooseRegister(row.deviceId)
                        onDone()
                      }}
                    />
                    {row.name}
                    {selected?.deviceId === row.deviceId && (
                      <span className="rmk-register__mark">
                        <CircleCheckBig size={14} aria-hidden="true" /> Выбрана
                      </span>
                    )}
                  </label>
                ) : null
              }
              workingText={vm.working?.deviceId === row.deviceId ? vm.working.text : null}
              busy={vm.busy}
              onCheck={() => actions.checkLink(row.deviceId)}
              onRelease={() => setReleasing(row.deviceId)}
              onJournal={() => actions.openJournal(row.deviceId)}
            />
          ))}
        </ul>
      </ActionFieldset>
      {releaseRow !== undefined && (
        <ReleaseDialog
          row={releaseRow}
          rights={vm.rights}
          onTakeover={(reason) => {
            actions.release(releaseRow.deviceId, 'takeover', reason)
            setReleasing(null)
          }}
          onCloseForeign={(reason) => {
            actions.release(releaseRow.deviceId, 'close-foreign', reason)
            setReleasing(null)
          }}
          onClose={() => setReleasing(null)}
        />
      )}
      {vm.journal !== null && <JournalDialog journal={vm.journal} onReload={actions.reload} onClose={actions.closeJournal} />}
    </>
  )
}

function registerSummary(row: RegisterRowVm): string {
  return row.shiftText === null ? row.name : `${row.name} · ${row.shiftText}`
}

/**
 * «Начало работы» (RP-FB22 3): кассир → код (если нужен) → вид цены → касса → «Открыть смену». Блоки идут сверху
 * вниз, следующий открывается, когда готов предыдущий; готовый свёрнут в строку с «изменить». На сервере до «Открыть
 * смену» ничего не создаётся: «назад» и F5 ничего не оставляют и не теряют.
 */
export function StartScreen({ vm, actions }: { vm: RmkVm; actions: RmkActions }) {
  const start = vm.start
  const [editing, setEditing] = useState<StepId | null>(null)
  const done = (step: StepId) => start.steps[step] === 'done' && editing !== step
  const finish = () => setEditing(null)
  const cashierLabel = start.cashier === null ? '' : start.cashier.kind === 'own' ? 'под своим именем' : start.cashier.name
  const code = start.code
  // Кассир и его код — один блок и одна строка: «Кассир: Иванова · код подтверждён» (зелёная галочка) либо «код не подтверждён» с полем ввода.
  const hasCode = code !== null && start.steps.code !== null
  const codeDone = hasCode && done('code')
  // Кассир выбран и подтверждён (кассиром нашей системы или прежним способом) — блок свёрнут в строку.
  const cashierReady = done('cashier') && (!hasCode || codeDone)
  const priceNumber = 2
  const registerNumber = 3

  return (
    <section className="aqsi-screen rmk-flow rmk-start" aria-labelledby="rmk-start-title">
      <header className="aqsi-toolbar">
        <h2 id="rmk-start-title">Начало работы</h2>
        <div className="aqsi-card__actions">
          {vm.source.alternatives > 1 && (
            <Button variant="ghost" icon={<ArrowLeftRight size={14} aria-hidden="true" />} actionId="aqsi.rmk.other-source" onClick={actions.switchSource}>
              Другой источник
            </Button>
          )}
        </div>
      </header>
      {vm.source.name !== 'Кабинет AQSI' && <p className="aqsi-caption">{vm.source.name}</p>}
      <FeedbackHelp help={vm.feedbackHelp} onIncidents={actions.openIncidents} />

      <ol className="rmk-steps" aria-label="Шаги начала работы">
        <Step number={1} title="Кассир">
          {!cashierReady && vm.ownCashiers !== undefined && (
            <>
              <OwnCashierStep own={vm.ownCashiers} actions={actions} onDone={finish} />
              <p className="aqsi-card__text rmk-step__legacy">Или прежним способом — кассир кабинета AQSI и его код:</p>
            </>
          )}
          {done('cashier') ? (
            <>
              <Summary
                label="Кассир"
                value={cashierLabel}
                note={start.resetNote}
                status={hasCode ? { ok: codeDone, text: codeDone ? 'код подтверждён' : 'код не подтверждён' } : undefined}
                onChange={() => setEditing('cashier')}
                changeLabel="Изменить кассира"
              />
              {hasCode && !codeDone && code !== null && <CodeStep code={code} actions={actions} />}
            </>
          ) : (
            <CashierStep start={start} actions={actions} onDone={finish} />
          )}
        </Step>

        {start.steps.price !== 'later' && (
          <Step number={priceNumber} title="Вид цены">
            {done('price') ? (
              <Summary label="Вид цены" value={start.priceTypeName} note={start.priceTypeResetNote} onChange={() => setEditing('price')} changeLabel="Изменить вид цены" />
            ) : (
              <>
                <PriceTypePicker
                  priceTypes={start.priceTypes}
                  busy={vm.busy}
                  onChoose={(id) => {
                    actions.chooseStartPriceType(id)
                    finish()
                  }}
                  onRetry={actions.reload}
                />
                {start.priceTypeResetNote !== null && <p className="aqsi-card__text">{start.priceTypeResetNote}</p>}
              </>
            )}
          </Step>
        )}

        {start.steps.register !== 'later' && (
          <Step number={registerNumber} title="Касса">
            {done('register') && start.registers.selected !== null ? (
              <Summary label="Касса" value={registerSummary(start.registers.selected)} onChange={() => setEditing('register')} changeLabel="Изменить кассу" />
            ) : (
              <RegisterStep vm={vm} actions={actions} onDone={finish} />
            )}
            {start.registerNote !== null && (
              <p className="aqsi-card__text" role="status">
                <ShieldAlert size={14} aria-hidden="true" /> {start.registerNote}
              </p>
            )}
          </Step>
        )}
      </ol>

      <div className="rmk-start__go">
        {start.action === 'continue' ? (
          <Button
            variant="primary"
            icon={<PlayCircle size={18} aria-hidden="true" />}
            actionId="aqsi.rmk.continue-shift"
            disabled={!start.canStart || vm.busy}
            disabledReason={start.blockedReason}
            onClick={actions.startWork}
          >
            Продолжить смену
          </Button>
        ) : (
          <Button
            variant="primary"
            icon={<ShoppingCart size={18} aria-hidden="true" />}
            actionId="aqsi.rmk.open-shift"
            disabled={!start.canStart || vm.busy}
            disabledReason={start.blockedReason}
            onClick={actions.startWork}
          >
            Открыть смену
          </Button>
        )}
        {start.blockedReason !== null && (
          <p className="aqsi-card__text" role="status">
            {start.blockedReason}
          </p>
        )}
      </div>

      <div className="rmk-flow__refresh">
        <Button icon={<RefreshCw size={16} aria-hidden="true" />} actionId="aqsi.rmk.reload" onClick={actions.reload}>
          Обновить
        </Button>
      </div>
    </section>
  )
}
